import { toJS } from 'mobx';
import { aloApiAuthFetch } from './alo-api-fetch';
import { LanguageState } from './country-language';
import { IS_NATIVE_APP_CLIENT, poll, checkIfCustomer } from './helpers';
import { ShippingCountryState } from './shipping';

const SIERRA_SESSION_TOKEN_KEY = 'sierra_auth_token';

export const clearSierraSession = () => {
  try {
    window.sierra?.endConversation?.();

    const domain = window.location.hostname;
    const cname = 'sierra-chat-session';
    document.cookie = `${cname}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    document.cookie = `${cname}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;

    sessionStorage.removeItem(SIERRA_SESSION_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to clear Sierra session on logout', error);
  }
};

const fetchSierraAuthToken = () => {
  return aloApiAuthFetch('/v1/proxy/sierra/token', {
    method: 'GET',
    credentials: 'include',
  })
    .then((sierraAuthTokenResponse) => {
      if (!sierraAuthTokenResponse.ok) {
        throw sierraAuthTokenResponse;
      }
      return sierraAuthTokenResponse.json().then((data: { token?: string }) => {
        const token = data?.token || null;
        if (token) sessionStorage.setItem(SIERRA_SESSION_TOKEN_KEY, token);
        return token;
      });
    })
    .catch((error) => {
      if (error.json) {
        error.json().then((errJson: unknown) => {
          console.error('Failed to fetch Sierra session token:', errJson);
        });
      } else {
        console.error('Network or other error:', error);
      }
      return null;
    });
};

const buildSierraConfig = async () => {
  const isMobile = window.innerWidth <= 440;
  const persistenceMode = isMobile ? 'tab' : 'cookie';
  const locale = `${toJS(LanguageState.languageCode)}-${toJS(ShippingCountryState.countryCode)}`;

  const config: Record<string, unknown> = {
    display: 'corner',
    hideOnClose: false,
    persistence: persistenceMode,
    canPrintTranscript: true,
    launcher: {
      backgroundColor: '#000000',
      textColor: '#ffffff',
      borderColor: '#cccccc',
      hoverBackgroundColor: '#ffffff',
      hoverTextColor: '#000000',
      hoverBorderColor: '#cccccc',
      text: '',
      icon: 'oval',
      notificationColor: 'ffffff',
      showUnreadIndicator: true,
      unreadIndicatorColor: '#000000',
      unreadIndicatorText: 'New message',
      unreadIndicatorIcon: 'circle',
      customStyle: {
        bottom: '10px',
        right: '10px',
        zIndex: '100',
      },
    },
    variables: {
      locale,
    },
    onOpen() {
      const sierraContainer = document.querySelector('[data-sierra-chat-container]');
      if (sierraContainer) {
        sierraContainer.setAttribute('translate', 'no');
        sierraContainer.classList.add('notranslate');
      }
    },
  };
  const isSierraCustomerTokenEnabled = window.getSiteSettings?.()?.sierraEnableCustomerToken || false;
  if (isSierraCustomerTokenEnabled && checkIfCustomer()) {
    const existingToken = sessionStorage.getItem(SIERRA_SESSION_TOKEN_KEY);
    const token = existingToken || (await fetchSierraAuthToken());

    if (token) {
      config.userIdentityToken = token;
      config.secrets = {
        AUTH_TOKEN: token,
      };
    }
  }

  return config;
};

export const initSierraLiveChat = async () => {
  if (IS_NATIVE_APP_CLIENT) return;

  const sierraConfig = await buildSierraConfig();

  poll(() => Boolean(window.sierra), 5000, 1000).then((isSierraDefined) => {
    if (isSierraDefined) {
      window.openLiveAgent = () => {
        window.sierra?.openChatModal?.();
      };
      window.sierra?.init?.(sierraConfig);
    }
  });
};
